// What a row in the shared-data console actually SAYS about a share.
//
// A share has three independent off-switches - revoked, expired, and
// simply turned off - and the console's whole job is to let an admin see
// which one applies before they press anything. Reading only `enabled`
// would show "Active" on a link that expired last month; reading only
// expiresAt would show "Active" on one that was revoked this morning.
export const ACTIVE = 'active';
export const REVOKED = 'revoked';
export const EXPIRED = 'expired';
export const OFF = 'off';

// ORDER MATTERS AND IS NOT ARBITRARY. Revoked is a deliberate act and
// outranks a date passing on its own; a share that was revoked AND has
// since expired is still best described as revoked, because that is what
// somebody DID. Expiry outranks "off" for the same reason in reverse: an
// expiry is a decision that was made in advance, "off" is just the state.
export function shareStatus(row, now = new Date()) {
  if (!row) return OFF;
  if (row.revokedAt) return REVOKED;
  const t = row.expiresAt ? new Date(row.expiresAt).getTime() : null;
  if (t !== null && !Number.isNaN(t) && t <= now.getTime()) return EXPIRED;
  if (row.enabled === false) return OFF;
  return ACTIVE;
}

export const STATUS_LABEL = {
  [ACTIVE]: 'Active',
  [REVOKED]: 'Revoked',
  [EXPIRED]: 'Expired',
  [OFF]: 'Turned off',
};
export const STATUS_COLOUR = {
  [ACTIVE]: 'green', [REVOKED]: 'red', [EXPIRED]: 'orange', [OFF]: 'default',
};

// "Extend" on a share that is not active does not do what the word
// promises, so the console says what will actually happen instead of
// letting somebody press it and find out.
export function extendCaveat(row, now = new Date()) {
  const s = shareStatus(row, now);
  if (s === REVOKED) return 'This share is revoked. A new expiry will not let anybody back in until it is re-enabled.';
  if (s === OFF) return 'This share is turned off. A new expiry takes effect only once it is on again.';
  return '';
}

// A share with no expiry never lapses. Saying "Never" is the honest word;
// an empty cell reads as missing data.
export function expiryLabel(row, now = new Date()) {
  if (!row || !row.expiresAt) return 'Never';
  const d = new Date(row.expiresAt);
  if (Number.isNaN(d.getTime())) return 'Unreadable date';
  const days = Math.ceil((d.getTime() - now.getTime()) / 864e5);
  if (days < 0) return `${d.toISOString().slice(0, 10)} (${Math.abs(days)}d ago)`;
  if (days === 0) return `${d.toISOString().slice(0, 10)} (today)`;
  return `${d.toISOString().slice(0, 10)} (in ${days}d)`;
}

// What this row IS, in the words the person uses for it. resourceType
// comes from the backend enum; anything unrecognised is shown as itself
// rather than mapped to a friendly lie.
const SUBJECT = {
  projectAreas: 'Project areas', sheet: 'Sheet', schedule: 'Schedule',
  issues: 'Issues list', tracking: 'Construction tracking',
  documentation: 'Documentation', issue: 'Issue',
};
export const subjectLabel = (row) => (row && (SUBJECT[row.resourceType] || row.resourceType)) || 'Unknown';

// Who is on the other end of this share. An issue's guests are named
// people; a link share is a token anybody holding it can use, and saying
// "1 link" rather than "1 person" is the difference between those.
export function recipientSummary(row) {
  if (!row) return '';
  if (row.kind === 'issue') {
    const g = row.guestCount || 0;
    const c = row.collaboratorCount || 0;
    const parts = [];
    if (c) parts.push(`${c} ${c === 1 ? 'user' : 'users'}`);
    if (g) parts.push(`${g} ${g === 1 ? 'guest' : 'guests'}`);
    if (row.enabled) parts.push('public link');
    return parts.length ? parts.join(', ') : 'nobody yet';
  }
  return row.accessCount ? `link, opened ${row.accessCount}x` : 'link, never opened';
}


// WHAT THE LIST IS DOING RIGHT NOW - and in particular, WHETHER IT KNOWS.
//
// FOUND BY DRIVING THE PAGE, 2026-09-26. The route 404'd (the backend had not
// been restarted) and the console rendered "0 active, 0 expired, 0 revoked,
// 0 turned off" over "Nothing is shared out of this company". A security
// console told an admin that nothing was shared while 528 things were. The
// toast that said otherwise had already faded.
//
// A failed load and an empty company are the SAME SHAPE - rows.length === 0 -
// and only this function is allowed to tell them apart. Everything the page
// says about counts, emptiness or scope has to go through it, because a zero
// it has not earned is worse than an error message.
export const LOADING = 'loading';
export const FAILED = 'failed';
export const EMPTY = 'empty';
export const FILTERED_EMPTY = 'filtered-empty';
export const ROWS = 'rows';

export function listState({ loading, error, total, shown }) {
  if (loading) return LOADING;
  if (error) return FAILED;          // BEFORE any count: we do not have counts
  if (!total) return EMPTY;
  if (!shown) return FILTERED_EMPTY;
  return ROWS;
}

// ONE SOURCE FOR THE FAILURE WORDS, in two pieces, because they are shown in
// two places with different amounts of context around them. The alert already
// has the title above it, so it takes the detail alone; the empty table has
// nothing above it, so it takes the title and the detail together. Written as
// one string and used in both, they read "This list could not be loaded. This
// list could not be loaded, so...".
export const FAILURE_TITLE = 'This list could not be loaded';

export function failureDetail(error) {
  return 'It is not showing anything - including shares that exist. '
    + (error ? 'The server said: ' + error + '. ' : '')
    + 'Press Refresh to try again. Do not read this as "nothing is shared".';
}

// The words for an empty table, which must never claim knowledge the page
// does not have.
export function emptyMessage(state, error) {
  if (state === FAILED) return FAILURE_TITLE + '. ' + failureDetail(error);
  if (state === FILTERED_EMPTY) return 'No share matches this filter.';
  return 'Nothing is shared out of this company.';
}

// Counts are only honest when we actually have the rows they describe.
export function countsLine(state, counts) {
  if (state === LOADING || state === FAILED) return '';
  return counts.active + ' active \u00b7 ' + counts.expired + ' expired \u00b7 '
    + counts.revoked + ' revoked \u00b7 ' + counts.off + ' turned off.';
}


// WHO OPENED THIS LINK - four states, and three of them are ways of knowing
// nothing that must not be confused with each other.
//
// The owner asked for this because the console said only "public link". We do
// collect the answer: an IssueGuest row carries a name and an email (both
// required) and joins to a share by resourceType + resourceId.
//
// THE ORDER BELOW IS THE POINT.
//  - NOT_REQUIRED comes FIRST because it is the most dangerous: a link that
//    never asks who you are, reported as having no visitors, is an all-clear
//    that is exactly backwards. It looks like the safest of the four.
//  - UNRECORDED exists because the data is in that state TODAY: three of
//    staging's four shares have been opened (accessCount above zero, a real
//    lastAccessedAt) with requiresGuestInfo true and no guest rows left -
//    the rows were deleted after the fact. Without this state they render as
//    "not opened", which is false about a link somebody used.
export const OPENED_BY_PEOPLE = 'opened-by-people';
// INVITED IS NOT OPENED, and the difference is three real names.
//
// A guest row is created in TWO ways: somebody identifies themselves at a
// link (routes/shares.js /identify), or somebody is SENT a link
// (utils/executiveLinks.linksFor, which creates one per subject and whose
// own comment calls it "the record of who was let in"). The row looks the
// same either way, so its EXISTENCE says a link was issued - never that it
// was used.
//
// On prod, 2026-09-22: three people at one company were sent links to four
// shares, and the console's first version would have reported them as having
// OPENED them. They had not. The evidence that an open really happened is
// that recordAccess ran - it pushes an ipAddress and moves lastAccessed past
// createdAt - and the model DEFAULTS accessCount to 1, so a count of 1 on
// its own proves nothing at all.
export const INVITED_NOT_OPENED = 'invited-not-opened';
export const NOT_OPENED = 'not-opened';
export const NOT_REQUIRED = 'identification-not-required';
export const UNRECORDED = 'opened-but-unrecorded';

// Did THIS visitor actually open it, or were they merely sent the link?
// Three independent signs that recordAccess ran; any one of them is enough,
// and the absence of all three is what an untouched invitation looks like.
export function hasOpened(v) {
  const g = v || {};
  if (g.ipCount > 0) return true;                       // recordAccess pushes one
  if ((g.accessCount || 0) > 1) return true;            // 1 is the model default
  const seen = g.lastAccessed ? new Date(g.lastAccessed).getTime() : 0;
  const made = g.firstSeen ? new Date(g.firstSeen).getTime() : 0;
  // the creation stamps are written milliseconds apart and can land either
  // side of each other, so a real later access has to be clear of them
  return !!(seen && made && seen - made > 5000);
}

export function openedState(row) {
  const r = row || {};
  const visitors = Array.isArray(r.visitors) ? r.visitors : [];
  if (r.requiresGuestInfo === false) return NOT_REQUIRED;   // first, deliberately
  if (visitors.some(hasOpened)) return OPENED_BY_PEOPLE;
  // rows exist but none of them opened it: a link was SENT, not used
  if (visitors.length) return INVITED_NOT_OPENED;
  if (r.accessCount > 0) return UNRECORDED;                 // opened, nobody left on record
  return NOT_OPENED;
}

const when = (d) => {
  if (!d) return '';
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '' : t.toISOString().slice(0, 10);
};

// The words. Each one says what we KNOW, and none of them says "nobody"
// unless nobody is the honest answer.
export function openedSummary(row) {
  const r = row || {};
  const state = openedState(r);
  if (state === NOT_REQUIRED) {
    return r.accessCount
      ? `Identification not required on this link - opened ${r.accessCount} time${r.accessCount === 1 ? '' : 's'}, but it never asks who.`
      : 'Identification not required on this link - anybody holding it can open it without giving a name.';
  }
  if (state === OPENED_BY_PEOPLE) {
    const opened = r.visitors.filter(hasOpened);
    const sent = r.visitors.length - opened.length;
    return `${opened.length} ${opened.length === 1 ? 'person has' : 'people have'} opened this link`
      + (sent ? `; ${sent} more ${sent === 1 ? 'was' : 'were'} sent it and has not.` : '.');
  }
  if (state === INVITED_NOT_OPENED) {
    const v = r.visitors;
    const when = v.map((g) => g.firstSeen).filter(Boolean).sort()[0];
    return `${v.length} ${v.length === 1 ? 'person was' : 'people were'} sent this link`
      + `${when ? ` on ${new Date(when).toISOString().slice(0, 10)}` : ''}`
      + `, and nobody has opened it.`;
  }
  if (state === UNRECORDED) {
    const last = when(r.lastAccessedAt);
    return `Opened ${r.accessCount} time${r.accessCount === 1 ? '' : 's'}${last ? `, last on ${last}` : ''}, `
      + 'but no visitor is on record - so this does not mean nobody.';
  }
  const made = when(r.createdAt);
  return `Not opened yet${made ? ` - created ${made}` : ''}.`;
}

// One visitor, for the list under the summary.
export function visitorLine(v) {
  const g = v || {};
  const name = g.name || g.email || 'unnamed visitor';
  const who = `${name}${g.email && g.email !== name ? ` <${g.email}>` : ''}`;
  if (!hasOpened(g)) {
    // NEVER say "last opened" about somebody who has not. The date on an
    // unopened row is when the link was SENT.
    const sent = when(g.firstSeen);
    return `${who} - sent${sent ? ` ${sent}` : ''}, not opened`;
  }
  const last = when(g.lastAccessed);
  const opens = g.accessCount > 1 ? `, ${g.accessCount} times` : '';
  return `${who}${last ? ` - last opened ${last}` : ''}${opens}`;
}

// IS THIS AN ANSWER, OR SOMETHING THAT ARRIVED WHERE AN ANSWER SHOULD BE?
//
// listState decides what the page SAYS, but it is only as good as the `error`
// it is handed - and the caller used to hand it none for a 200 carrying a body
// it could not read. `{}` became `rows: []` became "nothing is shared", on the
// one page whose job is to say who has access.
// So the question "did we actually get a list" lives here, next to the function
// whose correctness depends on it, rather than in a `|| []` at the call site.
// A proxy's HTML error page, a gateway notice and a truncated response all
// arrive as a 200 with a body that is not this shape.
export function usableList(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  return Array.isArray(payload.rows);
}
